// frontend/src/modules/invoicing/components/RecordPaymentDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { formatMoney } from '../../../design-system/format';
import { invoicesApi } from '../api';
import { amountDue, type Invoice, type InvoiceWithItems } from '../types';

const METHODS = ['bank_transfer', 'cash', 'card', 'cheque', 'other'] as const;
const METHOD_LABELS: Record<string, string> = {
  bank_transfer: 'Bank transfer', cash: 'Cash', card: 'Card', cheque: 'Cheque', other: 'Other',
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  invoice: Invoice;
  currency: string;
  onRecorded: (next: InvoiceWithItems) => void;
}

export function RecordPaymentDialog({ open, onOpenChange, invoice, currency, onRecorded }: Props) {
  const due = amountDue(invoice);
  const [amount, setAmount] = React.useState('');
  const [paidAt, setPaidAt] = React.useState('');
  const [method, setMethod] = React.useState<string>('bank_transfer');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setAmount(due > 0 ? String(due) : '');
      setPaidAt(new Date().toISOString().slice(0, 10));
      setMethod('bank_transfer');
      setNote('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Enter a positive amount'); return; }
    setBusy(true);
    try {
      const next = await invoicesApi.recordPayment(invoice.id, {
        amount: amt,
        paid_at: paidAt || null,
        method,
        note: note.trim() || null,
      });
      toast.success(next.status === 'paid' ? 'Payment recorded — invoice settled' : 'Partial payment recorded');
      onRecorded(next);
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Could not record payment', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record payment — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Amount<span className="text-destructive ml-0.5">*</span></Label>
            <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required autoFocus />
            <p className="text-xs text-muted-foreground">
              Amount due: <span className="font-mono">{formatMoney(due, currency)}</span> — a smaller amount marks the invoice partially paid.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Received on</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Note</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reference number, who paid, anything useful later." />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Recording…' : 'Record payment'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
